/**
 * Tabla de usuarios agrupada por sede (presentacional).
 *
 * Réplica del acordeón por sede del SSR (`users.html`, `progress/
 * explore_equipo_ssr.md`, §B.1): una sección por sede con sus usuarios
 * (Usuario/Nombre/Rol/Acciones). Marca al usuario actual ("Tú") y a los que tienen
 * permisos personalizados ("Personalizados"). Sin lógica de datos: recibe los
 * grupos y notifica las acciones por fila al padre.
 */
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import type { UserListItem, UsersGroup } from "../types";
import { RoleBadge } from "./RoleBadge";

interface UsersTableProps {
  groups: UsersGroup[];
  /** Username del usuario en sesión (para marcar "Tú"). */
  currentUsername: string | null;
  onEdit: (user: UserListItem) => void;
  onChangePassword: (username: string) => void;
  onManagePermissions: (username: string) => void;
  onDelete: (user: UserListItem) => void;
}

/** Etiqueta legible del encabezado de una sede (nombre o "Sin sede"). */
function sedeLabel(group: UsersGroup): string {
  if (group.sede?.nombre) {
    return group.sede.nombre;
  }
  if (group.sede_id != null) {
    return `Sede ${group.sede_id}`;
  }
  return "Sin sede";
}

export function UsersTable({
  groups,
  currentUsername,
  onEdit,
  onChangePassword,
  onManagePermissions,
  onDelete,
}: UsersTableProps) {
  if (groups.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-sm text-slate-500">
        No hay usuarios registrados.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <Card key={group.sede_id ?? "none"}>
          <CardHeader>
            <CardTitle>{sedeLabel(group)}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Usuario</TableHeaderCell>
                  <TableHeaderCell>Nombre</TableHeaderCell>
                  <TableHeaderCell>Rol</TableHeaderCell>
                  <TableHeaderCell className="text-right">Acciones</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.users.map((user) => {
                  const isCurrent = user.username === currentUsername;
                  return (
                    <TableRow key={user.username}>
                      <TableCell>
                        <span className="font-medium text-slate-900">
                          {user.username}
                        </span>
                        {isCurrent && (
                          <span className="ml-2 inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700">
                            Tú
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <RoleBadge role={user.role} />
                          {user.has_custom_permissions && (
                            <span className="inline-flex items-center rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-700">
                              Personalizados
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => onEdit(user)}
                          >
                            Editar
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => onChangePassword(user.username)}
                          >
                            Contraseña
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => onManagePermissions(user.username)}
                          >
                            Permisos
                          </Button>
                          <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            disabled={isCurrent}
                            onClick={() => onDelete(user)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
